// Данные склада: сборка одной позиции выдачи из нескольких запросов к 1С.
//
// Почему это один хук, а не пять: остаток без номенклатуры бессмыслен, а номенклатура
// без остатка вводит в заблуждение. Экрану нужна готовая строка, и собирать её из пяти
// независимо «моргающих» запросов он не должен. Свежесть при этом считается по САМОМУ
// СТАРОМУ ответу — см. mergeMeta.
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'

import { Unauthorized } from './client'
import { mergeMeta, odataGet, odataRetry } from './odata'
import type { OdataMeta } from './odata'
import {
  BALANCE_LIMIT, NOM_SELECT, NOM_TOP, REF_CHUNK, aggregate, byStock, chunk, guidOrFilter,
  layoutVariants, odataPath, searchFilter, toStockItem,
} from '../lib/stock'
import type { BalanceRow, Nomenclature, RefName, ReserveRow, StockItem } from '../lib/stock'

/** Минимальная длина запроса. На одном символе 1С вернёт полсклада и будет думать минуту. */
export const MIN_QUERY = 2

export interface StockSearchResult {
  items: StockItem[]
  /** Сколько кандидатов вернула 1С (до ограничения по остаткам). */
  found: number
  /** Для скольких позиций остатки реально запрошены. */
  withBalance: number
  /** Запрос, по которому нашлось, если исправляли раскладку. */
  corrected?: string
  /** Пояснение к выдаче — то же, что портал показывает над списком. */
  note: string
  /** Свежесть самого старого из ответов, из которых собрана выдача. */
  meta?: OdataMeta
}

const WAREHOUSES_PATH = odataPath('Catalog_Склады', { $select: 'Ref_Key,Description', $top: 200 })

/**
 * Справочник складов: ref → название. Меняется раз в год (ядро держит его 6 часов),
 * поэтому берём через общий кэш react-query — иначе каждый поиск слал бы за ним заново.
 */
function fetchWarehouses(qc: QueryClient): Promise<Record<string, string>> {
  return qc.fetchQuery({
    queryKey: ['1c', 'ut', WAREHOUSES_PATH, null],
    queryFn: () => odataGet<RefName>('ut', WAREHOUSES_PATH),
    staleTime: 30 * 60_000,
  }).then((r) => {
    const map: Record<string, string> = {}
    for (const w of r.rows) if (w.Ref_Key) map[w.Ref_Key] = w.Description || ''
    return map
  })
}

/** Поиск кандидатов по названию и артикулу. Папки групп отбрасываем: это не товар. */
async function findNomenclature(q: string) {
  const filter = searchFilter(q)
  if (!filter) return { rows: [] as Nomenclature[], meta: undefined as OdataMeta | undefined }
  const r = await odataGet<Nomenclature>('ut', odataPath('Catalog_Номенклатура', {
    $filter: filter, $select: NOM_SELECT, $top: NOM_TOP,
  }))
  return { rows: r.rows.filter((n) => !n.IsFolder && n.Ref_Key), meta: r.meta }
}

/**
 * Остатки по складам пачками по REF_CHUNK. Пачки идут параллельно — их не больше трёх
 * (BALANCE_LIMIT / REF_CHUNK), а у ядра лимит одновременных запросов в 1С равен четырём:
 * упереться в него одним поиском нельзя.
 */
async function fetchBalances(refs: string[]) {
  const parts = await Promise.all(chunk(refs, REF_CHUNK).map((c) =>
    odataGet<BalanceRow>('ut', odataPath('AccumulationRegister_ТоварыНаСкладах/Balance()', {
      $filter: guidOrFilter('Номенклатура_Key', c),
      $select: 'Номенклатура_Key,Склад_Key,ВНаличииBalance',
    }))))
  return { rows: parts.flatMap((p) => p.rows), metas: parts.map((p) => p.meta) }
}

/**
 * Резервы. Спрашиваем только по позициям, у которых есть остаток: резерв на пустой
 * позиции ничего не меняет, а каждый лишний запрос — это время в очереди к 1С.
 * Отказ не роняет выдачу: без резерва «свободно» просто равно «всего», и это видно.
 */
async function fetchReserves(refs: string[]) {
  if (!refs.length) return { rows: [] as ReserveRow[], metas: [] as Array<OdataMeta | undefined>, failed: false }
  try {
    const parts = await Promise.all(chunk(refs, REF_CHUNK).map((c) =>
      odataGet<ReserveRow>('ut', odataPath('AccumulationRegister_ТоварыКОтгрузке/Balance()', {
        $filter: guidOrFilter('Номенклатура_Key', c),
        $select: 'Номенклатура_Key,ВРезервеBalance',
      }))))
    return { rows: parts.flatMap((p) => p.rows), metas: parts.map((p) => p.meta), failed: false }
  } catch (e) {
    if (e instanceof Unauthorized) throw e
    return { rows: [] as ReserveRow[], metas: [] as Array<OdataMeta | undefined>, failed: true }
  }
}

/** Названия производителей. Не критично: нет — колонка просто пустая. */
async function fetchProducers(keys: Array<string | undefined>) {
  const uniq = [...new Set(keys.filter((k): k is string => !!k))].slice(0, 40)
  const filter = guidOrFilter('Ref_Key', uniq)
  if (!filter) return { map: {} as Record<string, string>, meta: undefined as OdataMeta | undefined }
  try {
    const r = await odataGet<RefName>('ut', odataPath('Catalog_Производители', {
      $filter: filter, $select: 'Ref_Key,Description',
    }))
    const map: Record<string, string> = {}
    for (const p of r.rows) if (p.Ref_Key) map[p.Ref_Key] = p.Description || ''
    return { map, meta: r.meta }
  } catch (e) {
    if (e instanceof Unauthorized) throw e
    return { map: {} as Record<string, string>, meta: undefined }
  }
}

async function searchStock(q: string, qc: QueryClient): Promise<StockSearchResult> {
  const metas: Array<OdataMeta | undefined> = []

  let found = await findNomenclature(q)
  metas.push(found.meta)
  let corrected: string | undefined

  // Ничего не нашли — возможно, слово набрано в чужой раскладке. Это один-два лишних
  // запроса, но только на неудачном поиске, а не на каждом.
  if (!found.rows.length) {
    for (const cand of layoutVariants(q)) {
      const retry = await findNomenclature(cand)
      metas.push(retry.meta)
      if (retry.rows.length) {
        found = retry
        corrected = cand
        break
      }
    }
  }

  if (!found.rows.length) {
    return { items: [], found: 0, withBalance: 0, note: 'Номенклатура с таким названием или артикулом в УТ не найдена.', meta: mergeMeta(metas) }
  }

  const warehouses = await fetchWarehouses(qc).catch(() => ({} as Record<string, string>))
  const withBalanceRows = found.rows.slice(0, BALANCE_LIMIT)
  const balances = await fetchBalances(withBalanceRows.map((n) => n.Ref_Key))
  metas.push(...balances.metas)

  const stocked = new Set(balances.rows.filter((b) => Number(b.ВНаличииBalance || 0) && b.Номенклатура_Key)
    .map((b) => b.Номенклатура_Key as string))
  const reserves = await fetchReserves([...stocked])
  metas.push(...reserves.metas)

  const producers = await fetchProducers(found.rows.map((n) => n.Производитель_Key))
  metas.push(producers.meta)

  const agg = aggregate(balances.rows, reserves.rows, warehouses)
  const known = new Set(withBalanceRows.map((n) => n.Ref_Key))
  const items = found.rows
    .map((n) => toStockItem(n, agg[n.Ref_Key], known.has(n.Ref_Key), producers.map))
    .sort(byStock)

  const notes: string[] = []
  if (corrected) notes.push(`Показано по исправленному запросу «${corrected}» (была другая раскладка клавиатуры).`)
  if (found.rows.length > BALANCE_LIMIT) {
    notes.push(`Найдено ${found.rows.length} позиций, остатки запрошены для первых ${BALANCE_LIMIT} — уточните запрос, чтобы увидеть остальные.`)
  } else if (!items.some((i) => i.have)) {
    notes.push('Позиции найдены, но остаток по ним нулевой.')
  }
  if (reserves.failed) notes.push('Резервы 1С не отдала — «свободно» показано без их учёта.')
  if (found.rows.length === NOM_TOP) notes.push(`1С вернула максимум ${NOM_TOP} совпадений — возможно, есть и другие.`)

  return {
    items,
    found: found.rows.length,
    withBalance: withBalanceRows.length,
    corrected,
    note: notes.join(' '),
    meta: mergeMeta(metas),
  }
}

/** Поиск по складу. Пустой/короткий запрос запроса в 1С не делает. */
export function useStockSearch(q: string) {
  const qc = useQueryClient()
  const term = q.trim()
  return useQuery({
    queryKey: ['stock-search', term],
    enabled: term.length >= MIN_QUERY,
    queryFn: () => searchStock(term, qc),
    retry: odataRetry,
    // Остатки в ядре живут 180 с; держать свой кэш дольше — показывать вчерашний день.
    staleTime: 60_000,
    placeholderData: (prev) => prev,   // не мигать пустотой, пока идёт следующий запрос
  })
}
